import {IReactionDisposer, autorun, makeAutoObservable, runInAction} from "mobx";
import {IOption} from "../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import {AMOUNT_TYPE, FEE_AMOUNT_OPTIONS_TRANSLATED, FEE_TYPE, IPolicyFee, SHIPPING_LABEL_FEE_TYPES} from "../../policies/policy-types";
import {RETURN_METHOD_TYPE} from "../../return-methods/return-method-types";
import {RESOLUTION_TYPE} from "../../resolutions/constants";
import {CreateRmaMainStore} from "./create-rma-main-store";
import OrderProductStore from "./order-product-store";
import OrderStore from "./order-store";
import RequestProductStore from "./request-product-store";
import requestProductStore, {ICreateRmaItemDto} from "./request-product-store";
import RmaAutoRefundStore, {IAutoRefundDto} from "./rma-auto-refund-store";
import RmaFeeStore from "./rma-fee-store";
import RmaFeesListStore from "./rma-fees-list-store";
import { CreateRmaEligibilityStore } from "./create-rma-eligibility-store";
import {IManagerCredentials} from "../../../manager-approval-modal/manager-credentials-stores";
import { CREATE_RMA_MODE } from "../../../services/api/create-rma-service";
import { ICRMAFeeDto } from "../../../models/User";
import CreateRmaDynamicShippingOptionsStore from "./create-rma-dynamic-shipping-store";

interface IRmaRequestChanges extends Partial<ICreateRmaDto> {
	auto_mark_as_done: boolean,
	is_auto_approved: boolean,
	send_customer_emails: boolean,
	selected_dynamic_shipping_option_id?: number | null;
}
export interface ICreateRmaDto {
	order_id: string;
	order_name: string;
    locale?: string;
	items: ICreateRmaItemDto[];
	return_fee: ICRMAFeeDto;
	label_fee: ICRMAFeeDto;
	auto_refund: IAutoRefundDto;
	auto_mark_as_done: boolean;
	is_auto_approved: boolean;
	send_customer_emails: boolean;
    payment_currency_code: string
	note?: string;
    manager_approval_credentials?: IManagerCredentials;
	/** When dynamic shipping is required, the option chosen in the pre-submit modal (server field name). */
	selected_dynamic_shipping_option_id?: number | null;
}


class RmaRequestStore {
	private _requestProductStoreList: RequestProductStore[] = [];
	private _rmaFeesListStore: RmaFeesListStore;
	private _rmaAutoRefundStore: RmaAutoRefundStore;
	private _model: IRmaRequestChanges;
    private _eligibilityStore: CreateRmaEligibilityStore;
    private _dynamicShippingOptionsStore: CreateRmaDynamicShippingOptionsStore;
    private _autorunDispose: IReactionDisposer;
    private _hideLabelFeeFields: boolean = false;
	
	constructor(private _orderStore: OrderStore, private _createRmaMainStore: CreateRmaMainStore) {
		makeAutoObservable(this, {}, {autoBind: true});

        this._model = {
			note: '',
			auto_mark_as_done: false,
			is_auto_approved: false,
			send_customer_emails: this._createRmaMainStore.settings.sendCustomerEmails,
            payment_currency_code: this._orderStore.paymentCurrencyCode,
			selected_dynamic_shipping_option_id: undefined,
		};

        this._rmaAutoRefundStore = new RmaAutoRefundStore({
            is_on: this._createRmaMainStore.settings.isAutoRefund,
            trigger: this._createRmaMainStore.settings.autoRefundTrigger,
            validated_qualifier: this._createRmaMainStore.settings.autoRefundValidatedQualifier,
            execute: this._createRmaMainStore.settings.autoRefundExecute,
            since_value: this._createRmaMainStore.settings.autoRefundSinceValue,
            since_unit: this._createRmaMainStore.settings.autoRefundSinceUnit
        });
        this._rmaFeesListStore = this._buildRmaFeesListStore();
        this.setDefaultsToAutomation();
        this._eligibilityStore = new CreateRmaEligibilityStore(this);
        this._dynamicShippingOptionsStore = new CreateRmaDynamicShippingOptionsStore();
        /** subscribing to changes in store to make requests for eligibility checks, 
         * needed for is out of policy functionality to update eligibility state every time something in request changes */
        this._autorunDispose = autorun(() => {
            if(this.eligibilityStore?.shouldCheck()) {
                this.eligibilityStore?.checkEligibility();
            }
        });
	}

    /** cleanup when class is disposed */
    public __destroy(): void {
        this._autorunDispose();
    }
    
    get paymentCurrencyCode(): string { return this._model.payment_currency_code ?? ''; }
	get requestProducts():RequestProductStore[] { return this._requestProductStoreList;}
	get rmaFeesListStore():RmaFeesListStore { return this._rmaFeesListStore; }
	get autoMarkAsDone(): boolean { return this._model.auto_mark_as_done; }
	get isAutoApprove(): boolean { return this._model.is_auto_approved; }
	get sendCustomerEmails(): boolean { return this._model.send_customer_emails; }
	get total(): number{ return this.getFeeSummary('returnFee') + this.getFeeSummary('labelFee') + this.itemsSubtotal + this.restockingFeeTotal + this.itemsAdjustments; }
	get note(): string | undefined { return this._model.note; }
	get itemsSubtotal(): number {
		let subtotal = 0;
		this._requestProductStoreList.forEach((RPS: requestProductStore)=>{
			subtotal += RPS.paidAmountSummary;
		})
		return subtotal;
	}
	get itemsAdjustments(): number {
		let subtotal = 0;
		this._requestProductStoreList.forEach((RPS: requestProductStore)=>{
			subtotal += RPS.adjustmentsSummary;
		})
		return subtotal;
	}
    get restockingFeeTotal() {
        return this.requestProducts.reduce((acc, product)=> acc += product.restockingFeeSummary ,0);
    }
	get autoRefundStore(){ return this._rmaAutoRefundStore; }
	get getRequestInEditMode(): RequestProductStore | undefined { return this.requestProducts.find(RPS => RPS.editMode); }
    get shouldDisableResetToDefault(): boolean { return this._model.auto_mark_as_done === this._createRmaMainStore.settings.autoMarkDone && this._rmaAutoRefundStore.shouldDisableResetToDefault;}
    get eligibilityStore(): CreateRmaEligibilityStore { return this._eligibilityStore; }
    get hideLabelFeeFields(): boolean { return this._hideLabelFeeFields; }
    get dynamicShippingOptionsStore(): CreateRmaDynamicShippingOptionsStore { return this._dynamicShippingOptionsStore; }
    
    private _getReturnFeeSettings(policyId: number|undefined): IPolicyFee|undefined { 
        if (!policyId) { 
            return undefined; 
        }
        return this._createRmaMainStore.settings.findPolicyFeesSettings(policyId).find(fee => fee.fee_type === FEE_TYPE.RETURN_FEE);
    }
    private _getLabelFeeSettings(policyId: number|undefined): IPolicyFee|undefined { 
        if (!policyId) { 
            return undefined; 
        }
        return this._createRmaMainStore.settings.findPolicyFeesSettings(policyId).find(fee => SHIPPING_LABEL_FEE_TYPES.includes(fee.fee_type as FEE_TYPE));
    }

    public async handleDynamicShippingOptions(): Promise<boolean> {
        const shouldProceed = await this._dynamicShippingOptionsStore.waitForSelection(this.requestModel());

        runInAction(() => {
            const selectedOptionId = this._dynamicShippingOptionsStore.selectedOptionId;
            if (selectedOptionId !== null) {
                this._model.selected_dynamic_shipping_option_id = selectedOptionId;
            }
        });
        if (!shouldProceed) {
            return false;
        }
        return true;
    }
    
    public feeAmountTypeOptions(feeType: FEE_TYPE|null|undefined, getTranslation: (key: string, scope: string) => string, scope: string): IOption[] {
        let options = FEE_AMOUNT_OPTIONS_TRANSLATED;
        if (feeType === FEE_TYPE.RETURN_FEE) {
            options = options.filter(o => [
                AMOUNT_TYPE.FIXED, AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE
            ].includes(o.value));
        }
        return this._translateIOptionArray(options, getTranslation, scope);
    }
    
    public shippingLabelFeesOptions(getTranslation: (key: string, scope: string) => string, scope: string):  IOption[]{
        
        const options = [
            {
                label: 'FEE_TYPE_COST',
                value: FEE_TYPE.COST,
                disabled: !this._isLabelCostSupported()
            },
            {
                label: 'FEE_TYPE_COST_PLUS_ITEM_FEE',
                value: FEE_TYPE.COST_PLUS_ITEM_FEE,
                disabled: !this._isLabelCostSupported()
            },
            {
                label: 'FEE_TYPE_COST_PLUS_SHIPMENT_FEE',
                value: FEE_TYPE.COST_PLUS_SHIPMENT_FEE,
                disabled: !this._isLabelCostSupported()
            },
            {
                label: 'FEE_TYPE_SHIPMENT_FEE',
                value: FEE_TYPE.SHIPMENT_FEE,
            }
        ];
        
        return this._translateIOptionArray(options, getTranslation, scope);
    }
    private _translateIOptionArray = (options: IOption[], getTranslation: (key: string, scope: string) => string, scope: string) => {
        return options.map(option => {
            return {
                ...option,
                label: getTranslation(option.label as string, scope)
            }
        });
    }
    
    
    getOrderProductAvailableQuantity(orderProductStore: OrderProductStore): number{
		let quantity = orderProductStore.quantity;
		let rmaQuantity = 0;
		// Count all products added to RMA (can be added multiple times)
		this.requestProducts.forEach(
			(RPS) => {
				rmaQuantity += (RPS.product === orderProductStore ? RPS.quantity! : 0);
			}
		);
		
		return (quantity - rmaQuantity);		
	}

	getOrderProductTotalPaidAmount(orderProductStore: OrderProductStore): number{
		let availableQuantity = this.getOrderProductAvailableQuantity(orderProductStore);
		return orderProductStore.paidAmount * availableQuantity;
	}
	
    addProductStore(productStore: RequestProductStore) {
        const sameItem = this._requestProductStoreList.find(item => item.isSame(productStore));
        if(sameItem){
            sameItem.setQuantity(sameItem.quantity! + productStore.quantity!);
        }
        else {
    		this._requestProductStoreList.push(productStore);
        }
        this._setFeesStoresData();
	}

	saveProductStore(store: RequestProductStore){
		store.setEditMode(false);
		this._requestProductStoreList = this._requestProductStoreList.map(RPS => RPS.editMode ? store : RPS);
        this._setFeesStoresData();
	}

	removeProduct(product: RequestProductStore){
		this._requestProductStoreList.splice(this._requestProductStoreList.indexOf(product), 1);
        this._setFeesStoresData();
	}

    private _setFeesStoresData() {
        /** existing functionality for G-mode: */
        if (this._createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE) {
            this._resetLabelFeeStore();
            return;
        }

        /** handle wave all fees policy rule */
        if (this.shouldWaveAllFees() || this._requestProductStoreList.length === 0) {
            this._rmaFeesListStore.labelFee.resetStore();
            this._rmaFeesListStore.returnFee.resetStore();
            return;
        }

        this._setReturnFeeStoreData();
        this._setLabelFeeStoreData();
    }

    private _setReturnFeeStoreData() {
        this._rmaFeesListStore.returnFee.resetStore();

        /** For return fee, we select the fee acording to the max amount */
        const productWithMaxCalculatedFeeAmount = this._requestProductStoreList.reduce((acc, product) => {
            return acc.returnFeeCalculatedAmount > product.returnFeeCalculatedAmount ? acc : product;
        }, this._requestProductStoreList[0]);

        const returnFeeSettings = this._getReturnFeeSettings(productWithMaxCalculatedFeeAmount.policyId);
        /** when there is no return fee on selected policy */
        if (!returnFeeSettings) {
            return;
        }

        /** If the max amount is of type "percentage"- we take the fee amount from the policy rule fee on settings (which is not calculated), 
         * but if the amount type is "fixed amount"- we will take the calculated (and converted) fee amount from getFeeData call, to show correct amount in cases of different payment&store currencies. */
        let returnFeeAmount = returnFeeSettings.amount;
        if (returnFeeSettings.amount_type === AMOUNT_TYPE.FIXED) {
            returnFeeAmount = productWithMaxCalculatedFeeAmount.returnFeeCalculatedAmount;
        }

        this._rmaFeesListStore.returnFee.setAmount(returnFeeAmount!);
        this._rmaFeesListStore.returnFee.setAmountType(returnFeeSettings.amount_type);
        this._rmaFeesListStore.returnFee.setCharged(returnFeeSettings.is_charged);
        this._rmaFeesListStore.returnFee.setFeeType(returnFeeSettings.fee_type);
        this._rmaFeesListStore.returnFee.setIsChargeTax(returnFeeSettings.charge_tax ?? false);
    }

    private _setLabelFeeStoreData() {
        /** 3 possible views: 
         * 1. If for ALL selected policy rules the amount type is cost- we show only "cost" on the DD, without numbers.
         * 2. If for ALL selected policy rules the amount type is fixed- we take the max amount.
         * 3. If it's cost + {X} or any mix- we close the label fee card and show the tooltip on summary block. */
        
        this._rmaFeesListStore.labelFee.resetStore();
        this._hideLabelFeeFields = false;

        const filteredProductsWithLabelFeeSettings = this._requestProductStoreList.filter(product => !!this._getLabelFeeSettings(product.policyId)?.is_charged);

        if (filteredProductsWithLabelFeeSettings.length === 0) {
            return;
        }

        const shouldShowCostView = ():boolean => {
            return filteredProductsWithLabelFeeSettings.every(product => this._getLabelFeeSettings(product.policyId)!.fee_type === FEE_TYPE.COST);
        };

        const shouldShowFixedView = ():boolean => {
            return filteredProductsWithLabelFeeSettings.every(product => this._getLabelFeeSettings(product.policyId)!.fee_type === FEE_TYPE.SHIPMENT_FEE);
        };

        if (shouldShowCostView()) {
            this._rmaFeesListStore.labelFee.setCharged(true);
            this._rmaFeesListStore.labelFee.setIsChargeTax(filteredProductsWithLabelFeeSettings.some(product => this._getLabelFeeSettings(product.policyId)!.charge_tax));
            this._rmaFeesListStore.labelFee.setFeeType(FEE_TYPE.COST);
            return;
        }

        if (shouldShowFixedView()) {
            const productWithMaxLabelFeeAmount = filteredProductsWithLabelFeeSettings.reduce((acc, product) => {
                return acc.labelFeeCalculatedAmount > product.labelFeeCalculatedAmount ? acc : product;
            }, filteredProductsWithLabelFeeSettings[0])

            const labelFeeSettings = this._getLabelFeeSettings(productWithMaxLabelFeeAmount.policyId);
            this._rmaFeesListStore.labelFee.setAmount(productWithMaxLabelFeeAmount.labelFeeCalculatedAmount);
            this._rmaFeesListStore.labelFee.setAmountType(labelFeeSettings!.amount_type!);
            this._rmaFeesListStore.labelFee.setCharged(labelFeeSettings!.is_charged!);
            this._rmaFeesListStore.labelFee.setFeeType(labelFeeSettings!.fee_type!);
            this._rmaFeesListStore.labelFee.setIsChargeTax(labelFeeSettings!.charge_tax!);
            return;
        }
        
        this._hideLabelFeeFields = true;    
        this._rmaFeesListStore.labelFee.setCharged(true);
        this._rmaFeesListStore.labelFee.setFeeType(FEE_TYPE.COST); // this is for showing the tooltip on summary block, not visible on UI since the fields are hidden.
        
    }

	
	getProductRequestToEdit(product: OrderProductStore, model?: ICreateRmaItemDto){
		return new RequestProductStore(product, this,this._createRmaMainStore.settings, model, this._orderStore);
	}
    
    setDefaultsToAutomation(){
        this.setAutoMarkAsDone(this._createRmaMainStore.settings.autoMarkDone);
        this.autoRefundStore.setDefaults();
    }

    private _buildRmaFeesListStore(): RmaFeesListStore {
        const { return_fee, label_fee } = this._createRmaMainStore.settings.createRmaFeeDefaults;
        return new RmaFeesListStore(return_fee, label_fee);
    }

    
    getConflictingReturnMethodLabel(returnMethodId: number): string | undefined {
       /*
       * Logic is this:
       * No shipment required goes with anything
       * All other three (paid label, dropoff, ship with any carrier) goe with no shipment require or the exact same return method id.
       */
        const returnMethodType = this._createRmaMainStore.settings.findReturnMethod(returnMethodId)?.type;
        if (returnMethodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED) {
            return undefined;
        }
        else {
            return this.requestProducts.find(item => {
                if(item.editMode){
                    return false;
                }
                const itemReturnMethodType = this._createRmaMainStore.settings.findReturnMethod(item.returnMethodId!)?.type;
                //combination is allowed
                if(itemReturnMethodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED){
                    return false;
                }
                //else - if not same return method id combination not allowed
                if(returnMethodId !== item.returnMethodId){
                    return true;
                }
                return false;
                
            })?.returnMethodLabel;
        }
    }
    private _getFeeCalculation(feeStore: RmaFeeStore){
        let fee = 0;
		/** TODO: this block of calculation should probable be moved to server */
        if(feeStore.amountType === AMOUNT_TYPE.FIXED){
            if(feeStore.feeType === FEE_TYPE.COST_PLUS_ITEM_FEE){
                fee = feeStore.amount * this.requestProducts.reduce((acc, p) => acc + p.quantity!, 0);
            }
            else {
                fee = feeStore.amount;
            }
        }
        else  {
            
            const amountToCalculate = feeStore.amountType === AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE ?
                this.requestProducts.reduce((acc, p) => acc + p.product.paidAmount * p.quantity!, 0) :
                this.requestProducts.reduce((acc, p) => acc + p.product.catalogPrice * p.quantity!, 0);
            
            fee = (feeStore.amount / 100) * amountToCalculate;
        }
        return fee;
    }
    
    private _getFeeLabelCalculation(feeStore: RmaFeeStore){
        let cost = 0;
        if(feeStore.feeType === FEE_TYPE.COST || feeStore.feeType === FEE_TYPE.COST_PLUS_SHIPMENT_FEE){
            //TODO: get label cost;
            return cost;
        }
        else {
            return 0;
        }
    }
    getFeeSummary(feeType: 'returnFee' | 'labelFee' ): number {
        const feeStore = this._rmaFeesListStore[feeType];
        let cost = this._getFeeLabelCalculation(feeStore);
        if(feeStore.feeType === FEE_TYPE.COST){
            return cost * -1;
        }
        let fee = this._getFeeCalculation(feeStore);
        return  (cost + fee) * -1;
    }
    

	setIsAutoApprove(value: boolean){ this._model.is_auto_approved = value;}
	
	setAutoMarkAsDone(value: boolean) { this._model.auto_mark_as_done = value; }
	setSendCustomerEmail(value: boolean) { this._model.send_customer_emails = value; }
	setNote(value: string) {
		this._model.note = value;
	}
	
	requestModel(): ICreateRmaDto {
		const orderStore = this._createRmaMainStore.orderStore;

        const items: ICreateRmaItemDto[] = []; 
        /** server expects not grouped items (quantity 1) */
        this.requestProducts.forEach(requestProduct => {
            for (let i = 0; i < requestProduct.quantity; i++) {;
                items.push({ ...requestProduct.requestModel(), quantity: 1 });
            }
        });
		
		let rmaRequest: ICreateRmaDto;
		rmaRequest = {
			...this._model,
			order_id: orderStore?.orderId ?? '',
			order_name: orderStore?.orderName ?? '',
            locale: orderStore?.locale,
			items: items,
			return_fee: this.rmaFeesListStore.returnFee.requestModel(),
			label_fee: this.rmaFeesListStore.labelFee.requestModel(),
			auto_refund: this.autoRefundStore.requestModel(),
            note: this._model.note !== '' ? this._model.note : null,
		} as ICreateRmaDto;

		return rmaRequest;
	}

    getConflictingResolutionLabel(resolutionId: number): string | undefined {
        /*
        * Refund to payment method - Can only be combined with Refund to payment method and refund to bank account
        * Refund to store credit - Can only be combined with Refund to store credit and refund to bank account
        * Refund to gift card - Can only be combined with Refund to gift card and refund to bank account
        * Refund to bank account - Can be combined with anything
        */
        const currentResolutionType = this._createRmaMainStore.settings.findResolution(resolutionId)?.resolution_type;
        if (currentResolutionType === RESOLUTION_TYPE.BankRefund) {
            return undefined;
        }
        else {
            return this.requestProducts.find(item => {
                if(item.editMode){
                    return false;
                }
                const resolutionType = this._createRmaMainStore.settings.findResolution(item.resolutionId)?.resolution_type;
                return resolutionType !== currentResolutionType && resolutionType !== RESOLUTION_TYPE.BankRefund;
            })?.resolutionLabel;
        }
    }
 
    
    
    public doesLabelFeeIncludeCost(): boolean {
        if (!this.rmaFeesListStore.labelFee.feeType) {
            return false;
        }
        return [FEE_TYPE.COST_PLUS_SHIPMENT_FEE, FEE_TYPE.COST, FEE_TYPE.COST_PLUS_ITEM_FEE].includes(this.rmaFeesListStore.labelFee.feeType);
    }
    private _isLabelCostSupported(): boolean {
        return this._requestProductStoreList.some(ps => ps.isLabelCostSupported());
    }

    public hasNoShipment(): boolean {
        if (!this._requestProductStoreList.length) {
            return false;
        }
        return this._requestProductStoreList.every(ps => ps.hasNoShipmentRequiredRm());
    }

    private _resetLabelFeeStore() {
        const isLabelCostFeeType = this.doesLabelFeeIncludeCost();
        if ((!this._isLabelCostSupported() && isLabelCostFeeType) || this.hasNoShipment()) {
            this._rmaFeesListStore.labelFee.resetStore();
        }
    }
    
	public shouldWaveAllFees(): boolean {
		return this._requestProductStoreList.some(product => product.hasWaveAllFeesPolicyRule());
	}
}

export default RmaRequestStore;